import { useState } from "react";
import { ExternalLink, Fish } from "lucide-react";
import { usePoll } from "@/lib/usePoll";
import { num, dt } from "@/lib/api";
import { Panel, Metric, Badge, Empty, PageTitle, Dot } from "@/components/common";

const FLOW = {
  exchange_inflow: ["Exchange inflow", "bearish", "Potential sell pressure (not confirmed)"],
  exchange_outflow: ["Exchange outflow", "bullish", "Potential accumulation (not confirmed)"],
  exchange_to_exchange: ["Exchange ↔ exchange", "neutral", "Internal/arbitrage movement"],
  labelled_transfer: ["Labelled transfer", "neutral", ""],
  unknown_transfer: ["Unknown transfer", "neutral", "Direction not inferable"],
};

export default function Whales() {
  const [src, setSrc] = useState("");
  const { data } = usePoll(`/whales${src ? `?source=${src}` : ""}`, 10000, [src]);
  const s = data?.summary;
  const items = data?.items || [];
  return (
    <div>
      <PageTitle title="Whale Tracker" sub="Real large BTC transactions from Whale Alert (≥ $1M, labelled) and the public Bitcoin mempool (≥ 100 BTC outputs, unlabelled). A large transfer is never assumed to be a buy or sell." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric testid="whale-total-24h" label="Transactions · 24h" value={s?.total ?? "—"} />
        <Metric testid="whale-inflow" label="Exchange inflow · 24h" value={`${num(s?.by_flow?.exchange_inflow?.btc ?? 0, 1)} BTC`} tone="down" sub={`${s?.by_flow?.exchange_inflow?.count ?? 0} tx`} />
        <Metric testid="whale-outflow" label="Exchange outflow · 24h" value={`${num(s?.by_flow?.exchange_outflow?.btc ?? 0, 1)} BTC`} tone="up" sub={`${s?.by_flow?.exchange_outflow?.count ?? 0} tx`} />
        <div className="panel p-4 space-y-2 font-mono text-xs">
          <div className="flex items-center gap-2"><Dot ok={data?.status?.whale_alert_connected} /> Whale Alert stream</div>
          <div className="flex items-center gap-2"><Dot ok={data?.status?.chain_ok} /> Blockchain mempool</div>
          {data?.status?.whale_alert_error && <div className="text-red-400 text-[10px]">{data.status.whale_alert_error}</div>}
        </div>
      </div>
      <Panel title="Large transactions" icon={Fish} testid="whale-table"
        right={<div className="flex gap-1">{[["", "All"], ["whale_alert", "Whale Alert"], ["blockchain_mempool", "Mempool"]].map(([v, l]) => (
          <button key={l} data-testid={`whale-filter-${l.toLowerCase().replace(" ", "-")}`} onClick={() => setSrc(v)} className={`font-mono text-[10px] px-2 py-1 rounded ${src === v ? "bg-[#F7931A] text-black" : "text-zinc-400 hover:bg-zinc-800"}`}>{l}</button>))}</div>}>
        {items.length === 0 ? <Empty>No whale transactions captured yet. Feeds are live; entries appear as they happen.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-xs">
              <thead><tr className="text-zinc-500 text-[10px] uppercase tracking-wider text-left border-b border-zinc-800">
                <th className="py-2 pr-3">Time</th><th className="pr-3">Source</th><th className="pr-3 text-right">BTC</th><th className="pr-3 text-right">USD</th><th className="pr-3">From → To</th><th className="pr-3">Observation</th><th>Tx</th></tr></thead>
              <tbody>
                {items.map((w) => {
                  const f = FLOW[w.flow] || FLOW.unknown_transfer;
                  return (
                    <tr key={w.key} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                      <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">{dt(w.ts)}</td>
                      <td className="pr-3 text-zinc-500">{w.source === "whale_alert" ? "Whale Alert" : "Mempool"}</td>
                      <td className="pr-3 text-right text-zinc-100">{num(w.btc, 2)}</td>
                      <td className="pr-3 text-right text-zinc-300">{w.usd ? `$${num(w.usd / 1e6, 2)}M` : "—"}</td>
                      <td className="pr-3 text-zinc-400 max-w-[220px] truncate">{w.from_label} → {w.to_label}</td>
                      <td className="pr-3"><Badge v={f[1]}>{f[0]}</Badge> <span className="text-zinc-600 text-[10px] hidden xl:inline">{f[2]}</span></td>
                      <td>{w.link ? <a href={w.link} target="_blank" rel="noreferrer" className="text-[#F7931A] inline-flex items-center gap-1 hover:underline">view <ExternalLink className="w-3 h-3" /></a> : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>)}
      </Panel>
    </div>
  );
}
